import * as React from 'react'
import { getClassNameModern } from '../../../../../../Shared/Frontend/utils/getClassNameFromStyle'
import { BGColors } from '../../modernBlocks.state'
import type { ActionProps, DisplayOptionProps, ImageSizesProps } from '../../modernBlocks.state'
import { SharedBlockLayout } from '../shared/sharedBlock.layout'
import type { CtaBlockClassNames } from './ctaWithBgColourBlock.state'
import { convertTitleForBackground, htmlDecode, unEntity } from '../../../utils/html'
import { slugify } from '../../../../../../Shared/Frontend/utils/slugify'

export const ctaBlockClassesDefault: CtaBlockClassNames = {
  // DEFAULTS
  block: 'block block--cta-default',
  inview: 'inview',
  inner: 'block__inner ls-gutter ls-spacing--s',
  header: 'block__header',
  label: 'block__label text--s',
  title: 'block__title title--bg',
  content: 'block__content',
  text: 'block__text',
  visual: 'block__visual',
  image: 'block__image',
  footerImage: 'block__footer ls-clickables--centered',
  footerText: 'block__footer ls-clickables--centered',
  clickableWrapper: 'block__action ls-clickable',
  clickablePrimary: 'block__button button button--primary button--on-red',
  clickableSecondary: 'block__button button button--secondary button--on-red',

  // BLOCK SPECIFIC
  blockContent: 'ls-container',
  blockFull: 'ls-container--l',
  blockImage: 'block--cta-image',
  blockText: 'block--cta-text',
  blockVisualInner: '',
  layoutImage: 'block__layout',
  layoutText: 'block__layout ls-centered ls-gutter ls-spacing',
}

export const ctaBlockClassesNew: CtaBlockClassNames = {
  // DEFAULTS
  block: 'block block--cta-new',
  inview: 'inview',
  inner: 'block__inner ls-gutter ls-spacing--s',
  header: 'block__header',
  label: 'block__label text--s',
  title: 'block__title title--m',
  content: 'block__content',
  text: 'block__text text--l',
  visual: 'block__visual',
  image: 'block__image',
  footerImage: 'block__footer ls-clickables',
  footerText: 'block__footer ls-clickables',
  clickableWrapper: 'block__action ls-clickable',
  clickablePrimary: 'block__button button button--primary',
  clickableSecondary: 'block__button button button--tertiary',

  // BLOCK SPECIFIC
  blockContent: 'ls-container',
  blockFull: 'ls-container--l',
  blockImage: 'block--cta-image',
  blockText: 'block--cta-text',
  blockVisualInner: 'block__visual--inner fit-picture',
  layoutImage: 'block__layout',
  layoutText: 'block__layout ls-centered ls-gutter ls-spacing',
}

export const CtaWithBgColourBlockLayout = {
  ColorSchemeClassName: (bgColour?: BGColors) => {
    let colorScheme: string = 'cta--red'
    return colorScheme
  },
  Inner: (props: {
    children?: React.ReactNode
    hasImage: boolean
    amountNested: number
    design: 'new' | 'default'
    displayOption: DisplayOptionProps
  }) => {
    const { amountNested, children, hasImage } = props
    const ctaBlockClasses = props.design === 'new' ? ctaBlockClassesNew : ctaBlockClassesDefault
    return (
      <div
        className={`${getClassNameModern(
          ctaBlockClasses,
          'inner',
          'mw-container-m mx-auto px-gutter'
        )} ${getClassNameModern(
          ctaBlockClasses,
          hasImage ? 'blockImage' : 'blockText',
          hasImage ? '' : 'text-center'
        )} ${getClassNameModern(
          props.design === 'new' ? ctaBlockClassesNew : ctaBlockClassesDefault,
          props.displayOption == 'full-width' ? 'blockFull' : 'blockContent',
          props.displayOption == 'full-width' ? 'container-fluid' : 'container'
        )}`}
      >
        <div className={getClassNameModern(ctaBlockClasses, hasImage === true ? 'layoutImage' : 'layoutText', 'row')}>
          {children}
        </div>
      </div>
    )
  },
  Content: (
    props: {
      hasImage: boolean
      title?: string
      label?: string
      intro?: string
      showActionAsLink: boolean
      design: 'new' | 'default'
    } & ActionProps
  ) => {
    const {
      hasImage,
      title,
      label,
      intro,
      showPrimary,
      showSecondary,
      primaryAction,
      secondaryAction,
      showActionAsLink,
    } = props
    const ctaBlockClasses = props.design === 'new' ? ctaBlockClassesNew : ctaBlockClassesDefault
    return (
      <div className={getClassNameModern(ctaBlockClasses, 'content', `${hasImage ? 'col-12 col-md-6' : 'col-12'}`)}>
        {(title || label) && (
          <header className={getClassNameModern(ctaBlockClasses, 'header', '')}>
            {label && (
              <p className={getClassNameModern(ctaBlockClasses, 'label', 'fs-6 text-primary text-uppercase')}>
                {label}
              </p>
            )}
            {title && (
              <h2
                id={slugify(htmlDecode(title))}
                className={getClassNameModern(ctaBlockClasses, 'title', 'h1 mb-3')}
                dangerouslySetInnerHTML={{
                  __html: props.design === 'new' ? title : unEntity(convertTitleForBackground(title)),
                }}
              />
            )}
          </header>
        )}
        {intro && (
          <div
            className={getClassNameModern(ctaBlockClasses, 'text', '')}
            dangerouslySetInnerHTML={{ __html: intro }}
          />
        )}
        {(showPrimary || showSecondary) && (
          <footer
            className={getClassNameModern(ctaBlockClasses, hasImage === true ? 'footerImage' : 'footerText', 'pt-5')}
          >
            {showPrimary && primaryAction && primaryAction.title && primaryAction.url && (
              <div className={getClassNameModern(ctaBlockClasses, 'clickableWrapper', 'd-block d-sm-inline-block')}>
                <SharedBlockLayout.Clickable
                  id="ctaPrimaryAction"
                  type="button"
                  link={primaryAction}
                  classNames={getClassNameModern(
                    ctaBlockClasses,
                    showActionAsLink === true ? 'clickableSecondary' : 'clickablePrimary',
                    'btn btn-primary btn-sm'
                  )}
                />
              </div>
            )}
            {showSecondary && secondaryAction && secondaryAction.title && secondaryAction.url && (
              <div
                className={getClassNameModern(
                  ctaBlockClasses,
                  'clickableWrapper',
                  'd-block d-sm-inline-block ms-sm-3 mt-3 mt-sm-0'
                )}
              >
                <SharedBlockLayout.Clickable
                  id="ctaSecondaryAction"
                  type="button"
                  link={secondaryAction}
                  classNames={getClassNameModern(ctaBlockClasses, 'clickableSecondary', 'btn btn-outline-primary')}
                />
              </div>
            )}
          </footer>
        )}
      </div>
    )
  },
  Visual: (props: { image?: ImageSizesProps['image']; design: 'new' | 'default' }) => {
    const { image } = props
    const ctaBlockClasses = props.design === 'new' ? ctaBlockClassesNew : ctaBlockClassesDefault
    return image ? (
      <div className={getClassNameModern(ctaBlockClasses, 'visual', 'col-12 col-md-6 mt-5 mt-md-0')}>
        <div className={getClassNameModern(ctaBlockClasses, 'blockVisualInner', '')}>
          <SharedBlockLayout.ModernImageSrcSet
            image={image}
            lazyload={true}
            priority={undefined}
            classNames={getClassNameModern(ctaBlockClasses, 'image', 'w-100')}
            pictureClassNames="block__picture"
            imageProportions={{
              width: '644',
              height: '483',
              sizes: '(min-width: 1440px) 644px, (min-width: 1024px) 50vw, 100vw',
            }}
          />
          {props.design === 'new' && (
            <svg
              className="block__visual--mask"
              height="241"
              viewBox="0 0 47 241"
              width="47"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path clipRule="evenodd" d="m0 0v241l46.8611-241z" fillRule="evenodd" />
            </svg>
          )}
        </div>
      </div>
    ) : (
      <></>
    )
  },
}
